import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
  InputAdornment,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Card,
  CardContent,
  CardActionArea,
} from '@mui/material';
import {
  Send as SendIcon,
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Warning as WarningIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RestartIcon,
  SkipNext as SkipIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  Settings as SettingsIcon,
  Description as DescriptionIcon,
  AttachMoney as MoneyIcon,
  AccountBalance as AccountBalanceIcon,
  LocalHospital as HospitalIcon,
  DirectionsCar as CarIcon,
  Security as SecurityIcon,
  Policy as PolicyIcon,
  Assessment as AssessmentIcon,
  Help as HelpIcon,
  Forum as ForumIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  setLoading,
  setLoadingMessages,
  setLoadingOlderMessages,
  setSendingMessage,
  setError,
  setChats,
  appendChats,
  setSelectedChatId,
  setMessages,
  prependMessages,
  addMessage,
  setSearch,
  setChatPage,
  addChatToTop,
  deleteChat as deleteChatAction,
  clearMessages,
  setTrees,
  setLoadingTrees,
  setTreeKey,
  setTreeName,
  setCurrentBotResponse,
  setInputEnabled,
  resetGuidedChatbot,
} from '../../store/slices/guidedChatbotSlice';
import {
  GET_GUIDED_CHATBOT_CHATS,
  GET_GUIDED_CHATBOT_MESSAGES,
  GET_ACTIVE_DECISION_TREES,
} from '../../graphql/queries/guidedChatbot.queries';
import {
  CHAT_WITH_GUIDED_BOT,
  DELETE_GUIDED_CHAT,
} from '../../graphql/mutations/guidedChatbot.mutations';
import {
  FindGuidedChatListInput,
  FindGuidedMessageListInput,
  GuidedChatsListResponse,
  GuidedMessagesListResponse,
  ActiveDecisionTreesResponse,
  ChatWithGuidedBotInput,
  ChatWithGuidedBotResponse,
  DeleteGuidedChatInput,
  DeleteGuidedChatResponse,
  GuidedChat,
  GuidedChatMessage,
  BotResponse,
  BotOption,
  UserInput,
} from '../../types/guidedChatbot.types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MainLayout from '../../components/Layout/MainLayout';
import './GuidedChatbot.css';

// ===== Dynamic MUI Icon mapping =====
const ICON_MAP: Record<string, React.ReactElement> = {
  People: <PeopleIcon />,
  Person: <PersonIcon />,
  Home: <HomeIcon />,
  Settings: <SettingsIcon />,
  Description: <DescriptionIcon />,
  AttachMoney: <MoneyIcon />,
  AccountBalance: <AccountBalanceIcon />,
  LocalHospital: <HospitalIcon />,
  DirectionsCar: <CarIcon />,
  Security: <SecurityIcon />,
  Policy: <PolicyIcon />,
  Assessment: <AssessmentIcon />,
  Help: <HelpIcon />,
  Forum: <ForumIcon />,
};

const getMuiIcon = (iconName: string | undefined | null): React.ReactElement => {
  if (!iconName) return <HelpIcon />;
  return ICON_MAP[iconName] || <HelpIcon />;
};

const GuidedChatbot: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    chats,
    selectedChatId,
    messages,
    loading,
    loadingMessages,
    loadingOlderMessages,
    sendingMessage,
    error,
    chatsPagination,
    messagesPagination,
    search,
    treeKey,
    treeName,
    trees,
    loadingTrees,
    currentBotResponse,
    inputEnabled,
  } = useAppSelector((state) => state.guidedChatbot);

  const [searchInput, setSearchInput] = useState('');
  const [messageInput, setMessageInput] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Delete state
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState<GuidedChat | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Context menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuChatId, setMenuChatId] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesAreaRef = useRef<HTMLDivElement>(null);
  const chatListRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ===== Queries & Mutations =====
  const [fetchChats] = useLazyQuery<
    { chatbotChats: GuidedChatsListResponse },
    { params: FindGuidedChatListInput }
  >(GET_GUIDED_CHATBOT_CHATS, { fetchPolicy: 'network-only' });

  const [fetchMessages] = useLazyQuery<
    { chatbotMessages: GuidedMessagesListResponse },
    { params: FindGuidedMessageListInput }
  >(GET_GUIDED_CHATBOT_MESSAGES, { fetchPolicy: 'network-only' });

  const [fetchActiveTrees] = useLazyQuery<
    { activeDecisionTrees: ActiveDecisionTreesResponse }
  >(GET_ACTIVE_DECISION_TREES, { fetchPolicy: 'network-only' });

  const [chatWithGuidedBotMutation] = useMutation<
    { chatWithGuidedBot: ChatWithGuidedBotResponse },
    { params: ChatWithGuidedBotInput }
  >(CHAT_WITH_GUIDED_BOT);

  const [deleteChatMutation] = useMutation<
    { deleteChatbotChat: DeleteGuidedChatResponse },
    { params: DeleteGuidedChatInput }
  >(DELETE_GUIDED_CHAT);

  // ===== Effects =====
  useEffect(() => {
    loadChats(1, false);
    loadActiveTrees();
    return () => {
      dispatch(resetGuidedChatbot());
    };
  }, []);

  useEffect(() => {
    loadChats(1, false);
  }, [search]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // ===== Load active decision trees =====
  const loadActiveTrees = async () => {
    try {
      dispatch(setLoadingTrees(true));
      const { data } = await fetchActiveTrees();
      if (data?.activeDecisionTrees?.success && data.activeDecisionTrees.data) {
        const sorted = [...data.activeDecisionTrees.data].sort(
          (a, b) => a.displayOrder - b.displayOrder
        );
        dispatch(setTrees(sorted));
      } else {
        dispatch(setError(data?.activeDecisionTrees?.message || 'Error al cargar árboles de decisión'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoadingTrees(false));
    }
  };

  // ===== Normalize metadata (snake_case from DB → camelCase) =====
  const normalizeMetadata = (raw: any): BotResponse | null => {
    if (!raw) return null;
    // If already camelCase (from live mutation response), return as-is
    if ('inputType' in raw) return raw as BotResponse;
    // Convert snake_case keys from stored JSON
    return {
      message: raw.message,
      inputType: raw.input_type ?? null,
      options: raw.options ?? null,
      skippable: raw.skippable ?? null,
      skipLabel: raw.skip_label ?? null,
      searchEnabled: raw.search_enabled ?? null,
      canGoBack: raw.can_go_back ?? null,
      canRestart: raw.can_restart ?? null,
      currentPath: raw.current_path ?? null,
    };
  };

  // ===== Load chats =====
  const loadChats = async (page: number, append: boolean) => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchChats({
        variables: {
          params: {
            search: search || null,
            page,
            pageSize: chatsPagination.pageSize,
          },
        },
      });
      if (data?.chatbotChats?.success && data.chatbotChats.data) {
        if (append) {
          dispatch(appendChats(data.chatbotChats.data));
        } else {
          dispatch(setChats(data.chatbotChats.data));
        }
        dispatch(setChatPage(page));
      } else {
        dispatch(setError(data?.chatbotChats?.message || 'Error al cargar chats'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  // ===== Load messages for existing chat =====
  const loadMessages = async (chatId: string) => {
    try {
      dispatch(setLoadingMessages(true));
      const { data } = await fetchMessages({
        variables: {
          params: { chatId, search: null, page: 1, pageSize: 50 },
        },
      });
      if (data?.chatbotMessages?.success && data.chatbotMessages.data) {
        dispatch(setMessages(data.chatbotMessages.data));
        // Find last AI message metadata to restore currentBotResponse
        const msgs = [...data.chatbotMessages.data.messages].sort(
          (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
        const lastAiMsg = [...msgs].reverse().find((m) => m.role === 'ai' && m.metadata);
        if (lastAiMsg?.metadata) {
          const botResp = normalizeMetadata(lastAiMsg.metadata);
          if (!botResp) return;

          if (botResp.inputType === 'final_response') {
            // Flow completed — show tree selector for new query
            dispatch(setCurrentBotResponse(null));
            dispatch(setInputEnabled(false));
            loadActiveTrees();
          } else {
            dispatch(setCurrentBotResponse(botResp));
            dispatch(setInputEnabled(
              botResp.inputType === 'text' ||
              (botResp.inputType === 'dynamic_select' && botResp.searchEnabled === true)
            ));
          }
        } else {
          // No AI metadata found — keep input disabled
          dispatch(setCurrentBotResponse(null));
          dispatch(setInputEnabled(false));
        }

        // Restore treeKey by matching first human message against available trees
        const firstHumanMsg = msgs.find((m) => m.role === 'human');
        if (firstHumanMsg && trees.length > 0) {
          const matchedTree = trees.find((t) => t.name === firstHumanMsg.content);
          if (matchedTree) {
            dispatch(setTreeKey(matchedTree.treeKey));
            dispatch(setTreeName(matchedTree.name));
          }
        }
      } else {
        dispatch(setError(data?.chatbotMessages?.message || 'Error al cargar mensajes'));
        dispatch(setLoadingMessages(false));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
      dispatch(setLoadingMessages(false));
    }
  };

  const loadOlderMessages = async () => {
    if (!selectedChatId || loadingOlderMessages || !messagesPagination.hasMorePages) return;
    const nextPage = messagesPagination.page + 1;
    try {
      dispatch(setLoadingOlderMessages(true));
      const el = messagesAreaRef.current;
      const prevScrollHeight = el?.scrollHeight || 0;
      const { data } = await fetchMessages({
        variables: {
          params: { chatId: selectedChatId, search: null, page: nextPage, pageSize: 50 },
        },
      });
      if (data?.chatbotMessages?.success && data.chatbotMessages.data) {
        dispatch(prependMessages(data.chatbotMessages.data));
        requestAnimationFrame(() => {
          if (el) el.scrollTop = el.scrollHeight - prevScrollHeight;
        });
      } else {
        dispatch(setError(data?.chatbotMessages?.message || 'Error al cargar mensajes anteriores'));
        dispatch(setLoadingOlderMessages(false));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
      dispatch(setLoadingOlderMessages(false));
    }
  };

  // ===== Chat select =====
  const handleChatSelect = (chatId: string) => {
    if (chatId === selectedChatId) return;
    dispatch(setSelectedChatId(chatId));
    dispatch(setCurrentBotResponse(null));
    dispatch(setInputEnabled(false));
    dispatch(setTreeKey(null));
    dispatch(setTreeName(null));
    loadMessages(chatId);
  };

  const handleNewConversation = () => {
    dispatch(clearMessages());
    setMessageInput('');
    loadActiveTrees();
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      dispatch(setSearch(value));
    }, 400);
  };

  // Infinite scroll
  const handleMessagesScroll = useCallback(() => {
    const el = messagesAreaRef.current;
    if (!el) return;
    if (el.scrollTop < 50 && messagesPagination.hasMorePages && !loadingOlderMessages) {
      loadOlderMessages();
    }
  }, [messagesPagination.hasMorePages, loadingOlderMessages, selectedChatId, messagesPagination.page]);

  const handleChatListScroll = useCallback(() => {
    const el = chatListRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight - scrollTop - clientHeight < 50 && chatsPagination.hasMorePages && !loading) {
      loadChats(chatsPagination.page + 1, true);
    }
  }, [chatsPagination.hasMorePages, chatsPagination.page, loading]);

  // ===== Send guided bot message =====
  const sendGuidedMessage = async (
    userMessage: string,
    userInput: UserInput | null,
    currentTreeKey?: string
  ) => {
    const effectiveTreeKey = currentTreeKey || treeKey;
    if (!effectiveTreeKey) return;

    // Add user message to chat
    const userMsg: GuidedChatMessage = {
      id: `temp-${Date.now()}`,
      chatId: selectedChatId || '',
      tenantId: '',
      userId: '',
      role: 'human',
      content: userMessage,
      feedback: null,
      metadata: userInput ? { userInput } : null,
      createdAt: new Date().toLocaleString('en-US', {
        month: '2-digit', day: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
      }),
      createdBy: null,
    };
    dispatch(addMessage(userMsg));
    dispatch(setSendingMessage(true));
    dispatch(setInputEnabled(false));
    setMessageInput('');

    try {
      const { data: responseData } = await chatWithGuidedBotMutation({
        variables: {
          params: {
            chatId: selectedChatId || null,
            treeKey: effectiveTreeKey,
            userMessage,
            userInput,
          },
        },
      });

      if (responseData?.chatWithGuidedBot?.success && responseData.chatWithGuidedBot.data) {
        const { chatId: returnedChatId, botResponse } = responseData.chatWithGuidedBot.data;

        // Add AI message with metadata
        const aiMsg: GuidedChatMessage = {
          id: `ai-${Date.now()}`,
          chatId: returnedChatId,
          tenantId: '',
          userId: '',
          role: 'ai',
          content: botResponse.message,
          feedback: null,
          metadata: botResponse,
          createdAt: new Date().toLocaleString('en-US', {
            month: '2-digit', day: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
          }),
          createdBy: null,
        };
        dispatch(addMessage(aiMsg));
        dispatch(setCurrentBotResponse(botResponse));

        // Enable input if needed
        if (botResponse.inputType === 'text' ||
            (botResponse.inputType === 'dynamic_select' && botResponse.searchEnabled)) {
          dispatch(setInputEnabled(true));
        } else {
          dispatch(setInputEnabled(false));
        }

        // If final_response, clear currentBotResponse so tree selector shows after
        if (botResponse.inputType === 'final_response') {
          dispatch(setCurrentBotResponse(null));
        }

        // If new conversation, set chatId and add to sidebar
        if (!selectedChatId && returnedChatId) {
          dispatch(setSelectedChatId(returnedChatId));
          const newChat: GuidedChat = {
            id: returnedChatId,
            tenantId: '',
            userId: '',
            title: treeName || userMessage,
            lastMessageAt: aiMsg.createdAt,
            createdAt: aiMsg.createdAt,
            createdBy: null,
          };
          dispatch(addChatToTop(newChat));
        } else if (selectedChatId) {
          const existingChat = chats.find((c) => c.id === selectedChatId);
          if (existingChat) {
            dispatch(addChatToTop({ ...existingChat, lastMessageAt: aiMsg.createdAt }));
          }
        }
      } else {
        const msg = responseData?.chatWithGuidedBot?.message || 'Error al obtener respuesta del chatbot';
        dispatch(setError(msg));
      }
    } catch (err: any) {
      dispatch(setError(err.message || 'Error de conexión con el servidor'));
    } finally {
      dispatch(setSendingMessage(false));
    }
  };

  // ===== Tree selection =====
  const handleTreeSelect = (selectedTreeKey: string, selectedTreeName: string) => {
    dispatch(setTreeKey(selectedTreeKey));
    dispatch(setTreeName(selectedTreeName));
    sendGuidedMessage(selectedTreeName, null, selectedTreeKey);
  };

  // ===== Option click =====
  const handleOptionSelect = (option: BotOption) => {
    dispatch(setCurrentBotResponse(null));
    sendGuidedMessage(option.label, { type: 'option_select', value: option.value });
  };

  // ===== Text input send =====
  const handleSendTextInput = () => {
    const trimmed = messageInput.trim();
    if (!trimmed || sendingMessage) return;

    // Check if in dynamic_select with searchEnabled - send as search
    if (currentBotResponse?.inputType === 'dynamic_select' && currentBotResponse?.searchEnabled) {
      sendGuidedMessage(`Buscar: ${trimmed}`, { type: 'text_input', value: trimmed });
    } else {
      sendGuidedMessage(trimmed, { type: 'text_input', value: trimmed });
    }
  };

  // ===== Navigation actions =====
  const handleGoBack = () => {
    dispatch(setCurrentBotResponse(null));
    sendGuidedMessage('Atrás', { type: 'go_back' });
  };

  const handleRestart = () => {
    dispatch(setCurrentBotResponse(null));
    sendGuidedMessage('Reiniciar', { type: 'restart' });
  };

  const handleSkip = () => {
    dispatch(setCurrentBotResponse(null));
    sendGuidedMessage(currentBotResponse?.skipLabel || 'Omitir', { type: 'skip' });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendTextInput();
    }
  };

  // ===== Context menu / delete =====
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, chatId: string) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuChatId(chatId);
  };
  const handleMenuClose = () => { setMenuAnchorEl(null); setMenuChatId(null); };
  const handleDeleteClick = () => {
    const chat = chats.find((c) => c.id === menuChatId);
    if (chat) { setChatToDelete(chat); setConfirmDeleteOpen(true); setDeleteError(null); }
    handleMenuClose();
  };
  const handleCancelDelete = () => { setConfirmDeleteOpen(false); setChatToDelete(null); setDeleteError(null); };
  const handleConfirmDelete = async () => {
    if (!chatToDelete) return;
    try {
      setDeleting(true); setDeleteError(null);
      const { data: responseData } = await deleteChatMutation({
        variables: { params: { chatId: chatToDelete.id } },
      });
      if (responseData?.deleteChatbotChat?.success) {
        dispatch(deleteChatAction(chatToDelete.id));
        setConfirmDeleteOpen(false); setChatToDelete(null);
        setSuccessMessage('Chat eliminado exitosamente');
      } else {
        const msg = responseData?.deleteChatbotChat?.message || 'Error al eliminar chat';
        const errs = responseData?.deleteChatbotChat?.errors || [];
        setDeleteError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
      }
    } catch (err: any) {
      setDeleteError(err.message || 'Error de conexión con el servidor');
    } finally { setDeleting(false); }
  };

  // ===== Render bot message with controls =====
  const renderBotMessage = (msg: GuidedChatMessage, isLastAiMessage: boolean) => {
    const metadata = normalizeMetadata(msg.metadata as any);
    const hasInteractiveControls = isLastAiMessage && metadata && metadata.inputType !== 'final_response';

    return (
      <div key={msg.id} className="guided-chat-message ai">
        <div>
          <div className="guided-chat-message-bubble">
            {metadata?.inputType === 'final_response' ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {msg.content}
              </ReactMarkdown>
            ) : (
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{msg.content}</Typography>
            )}

            {/* Render menu options as buttons */}
            {metadata?.inputType === 'menu' && metadata?.options && metadata.options.length > 0 && (
              <Box className="guided-bot-options" sx={{ mt: 1.5 }}>
                {metadata.options.map((opt: BotOption, idx: number) => (
                  <Button
                    key={`${opt.value}-${idx}`}
                    variant="outlined"
                    size="small"
                    className="guided-option-button"
                    disabled={!hasInteractiveControls || sendingMessage}
                    onClick={() => hasInteractiveControls && handleOptionSelect(opt)}
                    startIcon={opt.icon ? getMuiIcon(opt.icon) : undefined}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '20px',
                      mr: 1,
                      mb: 1,
                    }}
                  >
                    {opt.label}
                  </Button>
                ))}
              </Box>
            )}

            {/* Render dynamic_select as interactive table */}
            {metadata?.inputType === 'dynamic_select' && metadata?.options && metadata.options.length > 0 && (() => {
              const hasColumns = metadata.options.some((opt: BotOption) => opt.columns && opt.columns.length > 0);
              const isInteractive = hasInteractiveControls && !sendingMessage;

              if (hasColumns) {
                const headers = metadata.options[0].columns!.map((col) => col.header);
                return (
                  <Box className={`guided-select-table-wrapper ${!isInteractive ? 'disabled' : ''}`} sx={{ mt: 1.5 }}>
                    <table className="guided-select-table">
                      <thead>
                        <tr>
                          {headers.map((header, hIdx) => (
                            <th key={hIdx}>{header}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {metadata.options.map((opt: BotOption, idx: number) => (
                          <tr
                            key={`${opt.value}-${idx}`}
                            className={isInteractive ? 'clickable' : ''}
                            onClick={() => isInteractive && handleOptionSelect(opt)}
                          >
                            {opt.columns!.map((col, cIdx) => (
                              <td key={cIdx}>{col.value}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                );
              } else {
                return (
                  <Box className={`guided-select-list-wrapper ${!isInteractive ? 'disabled' : ''}`} sx={{ mt: 1.5 }}>
                    <ul className="guided-select-list">
                      {metadata.options.map((opt: BotOption, idx: number) => (
                        <li
                          key={`${opt.value}-${idx}`}
                          className={isInteractive ? 'clickable' : ''}
                          onClick={() => isInteractive && handleOptionSelect(opt)}
                        >
                          {opt.icon && <span className="guided-select-list-icon">{getMuiIcon(opt.icon)}</span>}
                          <span>{opt.label}</span>
                        </li>
                      ))}
                    </ul>
                  </Box>
                );
              }
            })()}

            {/* Empty state for dynamic_select with no results */}
            {metadata?.inputType === 'dynamic_select' && metadata?.options && metadata.options.length === 0 && (
              <Box sx={{ mt: 1.5, p: 2, textAlign: 'center', color: '#999', fontStyle: 'italic' }}>
                <Typography variant="body2">No se encontraron resultados</Typography>
              </Box>
            )}

            {/* Skip button inside message */}
            {hasInteractiveControls && metadata?.skippable && (
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant="text"
                  onClick={handleSkip}
                  disabled={sendingMessage}
                  startIcon={<SkipIcon />}
                  sx={{ textTransform: 'none', color: '#999' }}
                >
                  {metadata.skipLabel || 'Omitir'}
                </Button>
              </Box>
            )}
          </div>
          <div className="guided-chat-message-time">{msg.createdAt}</div>
        </div>
      </div>
    );
  };

  // ===== Determine if we should show tree selector =====
  const showTreeSelector = !selectedChatId && messages.length === 0 && !loadingMessages;
  // Also show after final_response when no currentBotResponse
  const showTreeSelectorAfterFinal = selectedChatId && messages.length > 0 && !currentBotResponse && !sendingMessage;
  const lastAiMsg = [...messages].reverse().find((m) => m.role === 'ai');
  const lastAiMetadata = normalizeMetadata(lastAiMsg?.metadata);
  const isAfterFinalResponse = showTreeSelectorAfterFinal && lastAiMetadata?.inputType === 'final_response';

  // Find the last AI message index for enabling controls
  const lastAiMsgId = lastAiMsg?.id;

  return (
    <MainLayout>
      <div className="guided-chatbot-container">
        {/* ===== LEFT SIDEBAR ===== */}
        <div className="guided-chat-sidebar">
          <div className="guided-chat-sidebar-header">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              fullWidth
              className="new-chat-button"
              onClick={handleNewConversation}
            >
              Nueva Conversación
            </Button>
            <TextField
              size="small"
              placeholder="Buscar chat..."
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: '#999' }} />
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div className="guided-chat-list-wrapper" ref={chatListRef} onScroll={handleChatListScroll}>
            {chats.map((chat) => (
              <div
                key={chat.id}
                className={`guided-chat-list-item ${selectedChatId === chat.id ? 'active' : ''}`}
                onClick={() => handleChatSelect(chat.id)}
              >
                <div className="guided-chat-list-item-content">
                  <span className="guided-chat-list-item-title">{chat.title}</span>
                  <span className="guided-chat-list-item-date">{chat.lastMessageAt}</span>
                </div>
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuOpen(e, chat.id)}
                  sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </div>
            ))}
            {loading && (
              <div className="guided-chat-list-loading"><CircularProgress size={24} /></div>
            )}
            {!loading && chats.length === 0 && (
              <div className="guided-chat-list-empty">No hay conversaciones</div>
            )}
          </div>
        </div>

        {/* ===== RIGHT - CHAT WINDOW ===== */}
        <div className="guided-chat-main">
          <div className="guided-chat-messages-area" ref={messagesAreaRef} onScroll={handleMessagesScroll}>
            {/* Tree selector - initial state */}
            {showTreeSelector && (
              <div className="guided-tree-selector">
                <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>
                  Chatbot Guiado
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Selecciona un tipo de consulta para comenzar
                </Typography>
                {loadingTrees ? (
                  <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
                ) : (
                  <div className="guided-tree-cards">
                    {trees.map((tree) => (
                      <Card key={tree.id} className="guided-tree-card" elevation={2}>
                        <CardActionArea onClick={() => handleTreeSelect(tree.treeKey, tree.name)}>
                          <CardContent className="guided-tree-card-content">
                            <Box className="guided-tree-card-icon">
                              {getMuiIcon(tree.icon)}
                            </Box>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {tree.name}
                            </Typography>
                            {tree.description && (
                              <Typography variant="body2" color="text.secondary">
                                {tree.description}
                              </Typography>
                            )}
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Loading messages */}
            {loadingMessages && (
              <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
                <CircularProgress />
              </Box>
            )}

            {/* Messages */}
            {!loadingMessages && messages.length > 0 && (
              <>
                {loadingOlderMessages && (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={24} />
                  </Box>
                )}

                {messages.filter((msg) => msg.role !== 'tool').map((msg) => {
                  if (msg.role === 'human') {
                    return (
                      <div key={msg.id} className="guided-chat-message human">
                        <div>
                          <div className="guided-chat-message-bubble">{msg.content}</div>
                          <div className="guided-chat-message-time">{msg.createdAt}</div>
                        </div>
                      </div>
                    );
                  }
                  // AI message
                  return renderBotMessage(msg, msg.id === lastAiMsgId);
                })}

                {sendingMessage && (
                  <div className="guided-typing-indicator">
                    <div className="dot" />
                    <div className="dot" />
                    <div className="dot" />
                  </div>
                )}

                {/* Show tree selector again after final_response */}
                {isAfterFinalResponse && (
                  <div className="guided-tree-selector-inline">
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 500, color: '#333' }}>
                      ¿Deseas realizar otra consulta?
                    </Typography>
                    {loadingTrees ? (
                      <Box display="flex" justifyContent="center" py={2}><CircularProgress size={24} /></Box>
                    ) : (
                      <div className="guided-tree-cards">
                        {trees.map((tree) => (
                          <Card key={tree.id} className="guided-tree-card" elevation={2}>
                            <CardActionArea onClick={() => handleTreeSelect(tree.treeKey, tree.name)}>
                              <CardContent className="guided-tree-card-content">
                                <Box className="guided-tree-card-icon">
                                  {getMuiIcon(tree.icon)}
                                </Box>
                                <Typography variant="subtitle2" fontWeight={600}>
                                  {tree.name}
                                </Typography>
                              </CardContent>
                            </CardActionArea>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* ===== Input Area ===== */}
          <div className="guided-chat-input-area">
            {/* Navigation buttons */}
            {currentBotResponse?.canGoBack && (
              <Button
                size="small"
                variant="outlined"
                onClick={handleGoBack}
                disabled={sendingMessage}
                startIcon={<ArrowBackIcon />}
                sx={{ textTransform: 'none', minWidth: 'auto' }}
              >
                Atrás
              </Button>
            )}
            {currentBotResponse?.canRestart && (
              <Button
                size="small"
                variant="outlined"
                onClick={handleRestart}
                disabled={sendingMessage}
                startIcon={<RestartIcon />}
                sx={{ textTransform: 'none', minWidth: 'auto' }}
              >
                Reiniciar
              </Button>
            )}

            <TextField
              className="guided-chat-input-field"
              placeholder={inputEnabled ? 'Escribe un mensaje...' : 'Selecciona una opción...'}
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyDown={handleKeyDown}
              multiline
              maxRows={6}
              inputRef={inputRef}
              disabled={!inputEnabled || sendingMessage}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '24px',
                  backgroundColor: inputEnabled ? '#f5f7fa' : '#eee',
                },
              }}
            />
            <IconButton
              className="guided-send-button"
              onClick={handleSendTextInput}
              disabled={!messageInput.trim() || sendingMessage || !inputEnabled}
            >
              <SendIcon />
            </IconButton>
          </div>
        </div>

        {/* Context Menu */}
        <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>Eliminar</MenuItem>
        </Menu>

        {/* Delete Confirmation Dialog */}
        <Dialog open={confirmDeleteOpen} onClose={handleCancelDelete} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <WarningIcon color="error" />
              Confirmar Eliminación
            </Box>
          </DialogTitle>
          <DialogContent>
            {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
            <Typography>
              ¿Está seguro que desea eliminar esta conversación? Esta acción no se puede deshacer.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete} variant="outlined" disabled={deleting}>Cancelar</Button>
            <Button onClick={handleConfirmDelete} variant="contained" color="error" disabled={deleting}
              startIcon={deleting ? <CircularProgress size={20} /> : null}
            >
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbars */}
        <Snackbar open={!!successMessage} autoHideDuration={6000} onClose={() => setSuccessMessage(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
            {successMessage}
          </Alert>
        </Snackbar>
        {error && (
          <Snackbar open={!!error} autoHideDuration={6000} onClose={() => dispatch(setError(null))}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert onClose={() => dispatch(setError(null))} severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          </Snackbar>
        )}
      </div>
    </MainLayout>
  );
};

export default GuidedChatbot;
